import { useEffect, useRef } from "react";
import { TICKS_PER_SECOND, unitStats, type UnitKind } from "@empire/core-sim";
import { TRIALS, trialById, trialLayout, type Trial } from "@empire/shared";
import { BoardView } from "../../pixi/BoardView.js";
import { BoardNotice } from "../components/BoardNotice.js";
import { budgetLines, useEditorStore, type Brush } from "../../store/editor.js";
import { unlocked, useTrialsStore } from "../../store/trials.js";
import { useSessionStore } from "../../store/session.js";
import { unitKindsFromPlan } from "../theme/glyphs.js";
import { UnitChips, Waves } from "./AttackPlanner.js";
import { TowerTray } from "./Tutorial.js";

/**
 * The Trials tab (29 Sep 2026): hand-made battles to play any time — the
 * answer to "a new village has nothing to do after one building". Each one
 * teaches one thing about towers and armies, and the first time each star is
 * earned it pays resources into the village.
 */
export function Trials(): JSX.Element {
  const openId = useTrialsStore((s) => s.openId);
  const load = useTrialsStore((s) => s.load);
  const session = useSessionStore((s) => s.status);

  useEffect(() => {
    if (session === "ready") void load();
  }, [session, load]);

  // Leaving the tab gives the editor and the planner back.
  useEffect(() => () => useTrialsStore.getState().close(), []);

  const trial = openId ? trialById(openId) : undefined;
  return trial ? <TrialView trial={trial} /> : <TrialList />;
}

function Stars({ n, size = "text-base" }: { n: number; size?: string }): JSX.Element {
  return (
    <span className={`${size} tracking-tight`} aria-label={`${n} of 3 stars`}>
      {[0, 1, 2].map((i) => (
        <span key={i} className={i < n ? "text-gold" : "text-panel-edge"}>
          ★
        </span>
      ))}
    </span>
  );
}

function TrialList(): JSX.Element {
  const progress = useTrialsStore((s) => s.progress);
  const open = useTrialsStore((s) => s.open);
  const total = TRIALS.reduce((n, t) => n + (progress[t.id] ?? 0), 0);

  return (
    <div className="flex h-full flex-col">
      <header className="empire-parchment shrink-0 border-b border-panel-edge px-4 py-3">
        <p className="text-[10px] uppercase tracking-[0.18em] text-gold">Play any time</p>
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="font-display text-xl text-ink">Trials</h2>
          <span className="text-xs tabular-nums text-ink-dim" data-testid="trials-total">
            ★ {total} / {TRIALS.length * 3}
          </span>
        </div>
        <p className="text-[11px] text-ink-dim">
          Puzzles that teach the battle. Each new star pays resources into your village.
        </p>
      </header>
      <ul className="min-h-0 flex-1 overflow-y-auto p-3" data-testid="trial-list">
        {TRIALS.map((trial, i) => {
          const stars = progress[trial.id] ?? 0;
          const open_ = unlocked(progress, i);
          const next = stars < 3 ? trial.rewards[stars]! : 0;
          return (
            <li key={trial.id} className="mb-2">
              <button
                type="button"
                disabled={!open_}
                data-testid={`trial-${trial.id}`}
                onClick={() => open(trial.id)}
                className="flex w-full items-center gap-3 rounded-xl border border-panel-edge bg-panel px-3 py-2.5 text-left enabled:hover:border-gold/60 disabled:opacity-45"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-bg-raised font-display text-sm text-gold">
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-ink">{trial.name}</span>
                    <KindBadge trial={trial} />
                  </span>
                  <span className="block text-[11px] leading-snug text-ink-dim">
                    {open_ ? trial.lesson : "Earn a star on the one before to open it."}
                  </span>
                </span>
                <span className="flex shrink-0 flex-col items-end">
                  <Stars n={stars} />
                  {open_ && next > 0 ? (
                    <span className="text-[10px] tabular-nums text-gold">+{next} each</span>
                  ) : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function KindBadge({ trial }: { trial: Trial }): JSX.Element {
  return (
    <span
      className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
        trial.kind === "attack" ? "bg-clay/30 text-ink" : "bg-ok/25 text-ink"
      }`}
    >
      {trial.kind === "attack" ? "Attack" : "Defend"}
    </span>
  );
}

/** What each star needs, in one line. */
function starRule(trial: Trial): string {
  if (trial.kind === "attack") {
    return `★ break in · ★★ lose ${trial.lost2} or fewer · ★★★ lose ${trial.lost3} or fewer`;
  }
  return `★ ${trial.reached1} or fewer get in · ★★ ${trial.reached2} or fewer · ★★★ nobody`;
}

function TrialView({ trial }: { trial: Trial }): JSX.Element {
  const phase = useTrialsStore((s) => s.phase);
  const close = useTrialsStore((s) => s.close);
  const best = useTrialsStore((s) => s.progress[trial.id] ?? 0);

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-center gap-2 border-b border-panel-edge bg-bg-raised px-2 py-2">
        <button
          type="button"
          data-testid="trial-back"
          onClick={close}
          className="h-10 shrink-0 rounded-lg bg-panel px-3 text-xs font-semibold text-ink-dim hover:text-ink"
        >
          ← Trials
        </button>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 truncate text-sm font-semibold text-ink">
            {trial.name} <KindBadge trial={trial} />
          </p>
          <p className="truncate text-[10px] text-ink-dim">{starRule(trial)}</p>
        </div>
        <Stars n={best} size="text-sm" />
      </header>
      <TrialBoard key={`${trial.id}-${phase}`} trial={trial} />
      {phase === "result" ? <ResultPanel trial={trial} /> : <PlanPanel trial={trial} />}
    </div>
  );
}

/** The board: the maze to break, the board to build on, or the battle. */
function TrialBoard({ trial }: { trial: Trial }): JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<BoardView | null>(null);
  const phase = useTrialsStore((s) => s.phase);
  const building = trial.kind === "defend" && phase === "plan";
  const layout = useEditorStore((s) => s.layout);
  const validation = useEditorStore((s) => s.validation);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let cancelled = false;
    let mounted: BoardView | undefined;
    const state = useTrialsStore.getState();
    const board = state.outcome?.layout ?? (building ? useEditorStore.getState().layout : trialLayout(trial));

    // Building is done from straight above; battles keep the isometric look.
    void BoardView.mount(el, board, { topDown: building }).then((v) => {
      if (cancelled) {
        v.destroy();
        return;
      }
      mounted = v;
      view.current = v;
      if (building) {
        const narrow = v.app.screen.width < 600;
        v.frameAction(1, [board.gate], 1, narrow ? v.proj.fingerTilePx : 0);
        v.on({
          onPaint: (at) => useEditorStore.getState().paint(at),
          onPaintEnd: () => useEditorStore.getState().commit(),
        });
        return;
      }
      v.frameAction();
      const outcome = useTrialsStore.getState().outcome;
      if (!outcome) return;
      v.setUnitKinds(unitKindsFromPlan(outcome.plan));
      let position = 0;
      v.app.ticker.add((ticker) => {
        const { frames, watching } = useTrialsStore.getState();
        if (frames.length === 0) return;
        const end = frames.length - 1;
        position = watching ? Math.min(position + ticker.deltaMS / (1000 / TICKS_PER_SECOND), end) : end;
        v.setReplay(frames, position, watching);
        if (watching && position >= end) useTrialsStore.getState().finishWatching();
      });
    });
    return () => {
      cancelled = true;
      view.current = null;
      mounted?.destroy();
    };
    // Mounted once: the parent remounts this board (by key) when the phase changes.
  }, [building, trial]);

  useEffect(() => {
    if (!building) return;
    view.current?.setLayout(layout, validation.ok ? [] : validation.error.tiles);
  }, [building, layout, validation]);

  return (
    <div className="relative min-h-0 flex-1 overflow-hidden">
      <div ref={host} className="h-full w-full touch-none [&>canvas]:block" data-testid="trial-board" />
      {building ? <BoardNotice /> : null}
      {building ? <BuildHud /> : null}
    </div>
  );
}

function BuildHud(): JSX.Element {
  const layout = useEditorStore((s) => s.layout);
  return (
    <div className="pointer-events-none absolute left-2 top-2 rounded bg-black/50 px-2 py-1 text-[11px] tabular-nums text-ink-dim">
      {budgetLines(layout)
        .filter((l) => l.allowed > 0)
        .map((l) => `${l.label} ${l.used}/${l.allowed}`)
        .join(" · ")}
    </div>
  );
}

const DEFEND_TOOLS: { brush: Brush; label: string }[] = [
  { brush: "road", label: "Road" },
  { brush: "select", label: "Towers" },
  { brush: "erase", label: "Erase" },
];

function PlanPanel({ trial }: { trial: Trial }): JSX.Element {
  const fight = useTrialsStore((s) => s.fight);
  const message = useTrialsStore((s) => s.message);
  const brush = useEditorStore((s) => s.brush);
  const setBrush = useEditorStore((s) => s.setBrush);
  const validation = useEditorStore((s) => s.validation);
  const clearBoard = useEditorStore((s) => s.clearBoard);

  return (
    <div
      className="flex max-h-[48%] shrink-0 flex-col border-t border-panel-edge bg-bg-raised"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <p className="shrink-0 px-3 pt-2 text-[11px] leading-snug text-ink-dim">{trial.lesson}</p>
      {trial.kind === "attack" ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <UnitChips only={Object.keys(trial.army) as UnitKind[]} />
          <Waves />
        </div>
      ) : (
        <div className="shrink-0">
          <p className="px-3 text-[11px] text-ink-dim">
            Coming:{" "}
            {trial.plan.waves
              .map((w, i) => `${i ? `+${w.offsetTicks / TICKS_PER_SECOND}s ` : ""}${summary(w.units)}`)
              .join(", then ")}
          </p>
          <div className="flex gap-1 overflow-x-auto no-scrollbar px-2 py-2">
            {DEFEND_TOOLS.map((tool) => (
              <button
                key={tool.brush}
                type="button"
                data-testid={`trial-tool-${tool.brush}`}
                onClick={() => setBrush(tool.brush)}
                className={`h-10 rounded-lg px-3.5 text-xs font-semibold ${
                  brush === tool.brush ? "bg-gold text-bg" : "bg-panel text-ink-dim"
                }`}
              >
                {tool.label}
              </button>
            ))}
            <button
              type="button"
              onClick={clearBoard}
              className="ml-auto h-10 rounded-lg bg-panel px-3 text-xs font-semibold text-ink-dim"
            >
              Clear
            </button>
          </div>
          {brush === "select" ? <TowerTray /> : null}
        </div>
      )}
      <div className="shrink-0 px-3 py-2">
        {message ? (
          <p className="mb-1.5 text-[11px] text-warn" data-testid="trial-message">
            {message}
          </p>
        ) : trial.kind === "defend" && !validation.ok ? (
          <p className="mb-1.5 text-[11px] text-ink-dim">{validation.error.message}</p>
        ) : null}
        <button
          type="button"
          data-testid="trial-fight"
          onClick={fight}
          className="h-11 w-full rounded-lg bg-gold text-xs font-bold text-bg"
        >
          {trial.kind === "attack" ? "Attack" : "Send them in"}
        </button>
      </div>
    </div>
  );
}

function summary(units: readonly UnitKind[]): string {
  const counts = new Map<UnitKind, number>();
  for (const u of units) counts.set(u, (counts.get(u) ?? 0) + 1);
  return [...counts].map(([k, n]) => `${n} ${unitStats(k).name}`).join(" + ");
}

function ResultPanel({ trial }: { trial: Trial }): JSX.Element {
  const outcome = useTrialsStore((s) => s.outcome)!;
  const watching = useTrialsStore((s) => s.watching);
  const finishWatching = useTrialsStore((s) => s.finishWatching);
  const reward = useTrialsStore((s) => s.reward);
  const saving = useTrialsStore((s) => s.saving);
  const saveError = useTrialsStore((s) => s.saveError);
  const retry = useTrialsStore((s) => s.retry);
  const open = useTrialsStore((s) => s.open);
  const close = useTrialsStore((s) => s.close);
  const progress = useTrialsStore((s) => s.progress);
  const index = TRIALS.findIndex((t) => t.id === trial.id);
  const next = TRIALS[index + 1];
  const { army } = outcome.result;

  if (watching) {
    return (
      <div className="shrink-0 border-t border-panel-edge bg-bg-raised px-3 py-3" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
        <button
          type="button"
          data-testid="trial-skip"
          onClick={finishWatching}
          className="h-11 w-full rounded-lg border border-panel-edge bg-panel text-xs font-semibold text-ink-dim"
        >
          Skip to the result
        </button>
      </div>
    );
  }

  const line =
    trial.kind === "attack"
      ? outcome.result.outcome === "breach"
        ? `You broke in and lost ${army.lost} of ${army.sent}.`
        : `Held. None of your ${army.sent} got through.`
      : army.reached === 0
        ? `Held! Not one of ${army.sent} got in.`
        : `${army.reached} of ${army.sent} got in.`;

  const paid = reward ? reward.wood : null;
  return (
    <div
      className="shrink-0 border-t border-panel-edge bg-bg-raised px-3 py-3"
      style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      data-testid="trial-result"
    >
      <div className="flex items-center gap-3">
        <Stars n={outcome.stars} size="text-3xl" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">{line}</p>
          <p className="text-[11px] text-ink-dim" data-testid="trial-reward">
            {saving
              ? "Saving…"
              : saveError
                ? saveError
                : paid && paid > 0
                  ? `+${paid} of each resource to your village`
                  : outcome.stars === 0
                    ? "No star this time."
                    : "Already earned — try for more stars."}
          </p>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          data-testid="trial-retry"
          onClick={retry}
          className="h-11 flex-1 rounded-lg border border-panel-edge bg-panel text-xs font-semibold text-ink"
        >
          Try again
        </button>
        {next && unlocked(progress, index + 1) ? (
          <button
            type="button"
            data-testid="trial-next"
            onClick={() => open(next.id)}
            className="h-11 flex-1 rounded-lg bg-gold text-xs font-bold text-bg"
          >
            Next trial
          </button>
        ) : (
          <button
            type="button"
            onClick={close}
            className="h-11 flex-1 rounded-lg bg-gold text-xs font-bold text-bg"
          >
            All trials
          </button>
        )}
      </div>
    </div>
  );
}
